import React from 'react';
import {
  NEUTRAL_ORB_COLOR,
  NOTE_SYMBOLS,
  orbCoreBackground,
  orbHalo,
  orbNotePositions,
} from '../../../vibe/orbVisual';

const NOTE_LAYOUT = orbNotePositions(NOTE_SYMBOLS.length, 40);

function usePrefersReducedMotion() {
  const [reduce, setReduce] = React.useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });

  React.useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => setReduce(media.matches);
    apply();
    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', apply);
      return () => media.removeEventListener('change', apply);
    }
    media.addListener?.(apply);
    return () => media.removeListener?.(apply);
  }, []);

  return reduce;
}

function displayTitle(songTitle) {
  if (typeof songTitle !== 'string') return '';
  return songTitle.trim();
}

/** Blended Crowd Vibe orb. Weighted Feel mix paints the core; no circumference markers. */
export default function CrowdFeelRing({ ring, playing = false, songTitle = '' }) {
  const reduceMotion = usePrefersReducedMotion();
  const animate = Boolean(playing) && !reduceMotion;
  const mixColor = ring?.mixColor || NEUTRAL_ORB_COLOR;
  const noteColor = ring?.noteColor || mixColor;
  const anchors = ring?.anchors || [];
  const glowColors = anchors.length ? anchors.map((item) => item.color) : [mixColor];
  const title = displayTitle(songTitle);

  return (
    <div className="relative mx-auto aspect-square w-full max-w-[22rem] overflow-visible lg:max-w-[26rem]">
      <div
        className={`crowd-orb absolute inset-[8%] rounded-full ${animate ? 'crowd-orb--live' : ''}`}
        style={{ boxShadow: orbHalo(glowColors) }}
      >
        <div
          data-testid="crowd-feel-ring"
          data-feel-ring={mixColor}
          className="absolute inset-0 rounded-full"
          style={{ background: orbCoreBackground([mixColor, ...glowColors.slice(0, 4)]) }}
        />
        <div className="crowd-orb__sheen pointer-events-none absolute inset-0 rounded-full" />
        <div className="pointer-events-none absolute inset-[18%] z-[1] flex items-center justify-center overflow-hidden px-1 text-center">
          {title ? (
            <p
              data-testid="crowd-orb-title"
              className="line-clamp-4 max-w-full break-words text-sm font-semibold leading-snug text-white"
            >
              {title}
            </p>
          ) : null}
        </div>
      </div>

      <div className="pointer-events-none absolute inset-0 z-[2]" aria-hidden="true">
        {NOTE_SYMBOLS.map((symbol, index) => (
          <span
            key={symbol}
            className={`vibe-note absolute text-sm font-bold ${animate ? `vibe-note--live vibe-note--${index % 3}` : ''}`}
            style={{
              top: NOTE_LAYOUT[index].top,
              left: NOTE_LAYOUT[index].left,
              color: noteColor,
              textShadow: `0 0 8px ${noteColor}88`,
              animationDelay: animate ? `${index * 0.18}s` : undefined,
            }}
          >
            {symbol}
          </span>
        ))}
      </div>
    </div>
  );
}
